import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
    getAssessment,
    getChecklist,
    getReport,
    getScores,
} from '~/api/assessment'
import type {
    AssessmentReport,
    AssessmentResponse,
    ChecklistItem,
    ItemScore,
} from '~/api/types'
import { scaleLabel } from '~/components/assessment/labels'

const VERDICT_LABEL: Record<string, string> = {
    적합: '적합',
    미흡: '미흡',
    부적합: '부적합',
    pass: '적합',
    partial: '미흡',
    fail: '부적합',
}
const PASS_LABEL: Record<string, string> = {
    high: '높음',
    medium: '보통',
    low: '낮음',
}

/**
 * 인쇄 전용 평가 보고서 — 앱 크롬 없이 A4 문서로 렌더하고 자동으로 인쇄(브라우저 PDF 저장)한다.
 * 새 탭으로 열려 "PDF로 저장"하면 화면 캡처가 아닌 선택 가능한 텍스트의 정식 보고서가 된다.
 */
export function ReportPrintPage() {
    const { id } = useParams()
    const [assessment, setAssessment] = useState<AssessmentResponse | null>(
        null
    )
    const [report, setReport] = useState<AssessmentReport | null>(null)
    const [scores, setScores] = useState<ItemScore[]>([])
    const [checklist, setChecklist] = useState<ChecklistItem[]>([])
    const [ready, setReady] = useState(false)

    useEffect(() => {
        if (!id) return
        Promise.all([
            getAssessment(id),
            getReport(id).catch(() => null),
            getScores(id).catch(() => []),
            getChecklist(id).catch(() => []),
        ])
            .then(([a, r, s, c]) => {
                setAssessment(a)
                setReport(r)
                setScores(s)
                setChecklist(c)
            })
            .finally(() => setReady(true))
    }, [id])

    // 데이터가 준비되면 자동 인쇄 다이얼로그(사용자가 PDF로 저장).
    useEffect(() => {
        if (ready && assessment) {
            const t = setTimeout(() => window.print(), 400)
            return () => clearTimeout(t)
        }
    }, [ready, assessment])

    if (!ready) {
        return (
            <p style={{ padding: 40, fontFamily: 'sans-serif' }}>
                보고서를 준비하는 중…
            </p>
        )
    }
    if (!assessment) {
        return (
            <p style={{ padding: 40, fontFamily: 'sans-serif' }}>
                보고서를 불러오지 못했어요.
            </p>
        )
    }

    const titleById = new Map(checklist.map((c) => [c.id, c.title]))
    const partById = new Map(checklist.map((c) => [c.id, c.part]))
    const total = assessment.totalScore ?? report?.content?.stats?.totalScore
    const max = assessment.maxScore ?? report?.content?.stats?.maxScore
    const pass =
        assessment.passLikelihood ?? report?.content?.stats?.passLikelihood
    const today = new Date().toISOString().slice(0, 10)
    const counts = scores.reduce(
        (acc, s) => {
            if (s.verdict === '적합') acc.pass += 1
            else if (s.verdict === '미흡') acc.partial += 1
            else if (s.verdict === '부적합') acc.fail += 1
            return acc
        },
        { pass: 0, partial: 0, fail: 0 }
    )

    return (
        <div className='report-root'>
            <style>{PRINT_CSS}</style>

            <div className='report-toolbar'>
                <button onClick={() => window.print()}>
                    PDF로 저장 / 인쇄
                </button>
                <span>브라우저 인쇄창에서 «PDF로 저장»을 선택하세요.</span>
            </div>

            <article className='report-page'>
                <header className='report-head'>
                    <div className='brand'>PAI · HACCP AI</div>
                    <h1>HACCP 사전 평가 보고서</h1>
                    <p className='subtitle'>{assessment.title}</p>
                    <table className='meta'>
                        <tbody>
                            <tr>
                                <th>평가 범위</th>
                                <td>
                                    {assessment.classification ?? '분류 미정'} ·{' '}
                                    {scaleLabel(assessment.scale)}
                                </td>
                                <th>발행일</th>
                                <td>{today}</td>
                            </tr>
                            <tr>
                                <th>업종</th>
                                <td colSpan={3}>
                                    {assessment.industry ?? '—'}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </header>

                <section className='kpis'>
                    <div className='kpi'>
                        <span>총점</span>
                        <b>
                            {total ?? '—'}
                            {max != null ? ` / ${max}` : ''}
                        </b>
                    </div>
                    <div className='kpi'>
                        <span>합격 가능성</span>
                        <b>{pass ? (PASS_LABEL[pass] ?? pass) : '—'}</b>
                    </div>
                    <div className='kpi'>
                        <span>적합 / 미흡 / 부적합</span>
                        <b>
                            {counts.pass} / {counts.partial} / {counts.fail}
                        </b>
                    </div>
                </section>

                {report?.summary || report?.content?.narrative ? (
                    <section className='block'>
                        <h2>종합 총평</h2>
                        <p className='narrative'>
                            {report?.content?.narrative || report?.summary}
                        </p>
                    </section>
                ) : null}

                {report?.content?.gaps && report.content.gaps.length > 0 ? (
                    <section className='block'>
                        <h2>주요 미흡 사항</h2>
                        <ul className='gaps'>
                            {report.content.gaps.map((g, i) => (
                                <li key={i}>{g}</li>
                            ))}
                        </ul>
                    </section>
                ) : null}

                {scores.length > 0 ? (
                    <section className='block'>
                        <h2>항목별 채점 결과</h2>
                        <table className='scores'>
                            <thead>
                                <tr>
                                    <th>PART</th>
                                    <th>평가항목</th>
                                    <th>판정</th>
                                    <th>점수</th>
                                </tr>
                            </thead>
                            <tbody>
                                {scores.map((s) => (
                                    <tr key={s.itemId}>
                                        <td className='nowrap'>
                                            {partById.get(s.itemId) ?? '—'}
                                        </td>
                                        <td>
                                            {titleById.get(s.itemId) ??
                                                s.itemId}
                                        </td>
                                        <td className='nowrap'>
                                            {s.verdict
                                                ? (VERDICT_LABEL[s.verdict] ??
                                                  s.verdict)
                                                : '—'}
                                        </td>
                                        <td className='nowrap num'>
                                            {s.score ?? 0} / {s.maxScore}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                ) : null}

                <footer className='report-foot'>
                    본 보고서는 HACCP AI가 사전 자가진단 목적으로 생성한
                    참고자료이며, 최종 인증 판단은 관할 기관·심사로 확정됩니다.
                    · PAI(PublicAI) · 발행 {today}
                </footer>
            </article>
        </div>
    )
}

const PRINT_CSS = `
/* 앱 셸(app.css)의 html,body,#root{height:100%}·body{overflow:hidden}를 덮어
   인쇄 전용 문서가 세로로 스크롤되게 한다(#26 스크롤 불가 수정, 유지하기 보고서와 동일 패턴). */
html,body,#root { height:auto; min-height:100%; overflow:visible; }
.report-root { background:#f3f4f6; min-height:100vh; padding:24px; box-sizing:border-box;
  font-family: 'Pretendard', system-ui, -apple-system, sans-serif; color:#1f2430; }
.report-toolbar { max-width:210mm; margin:0 auto 16px; display:flex; align-items:center; gap:12px; }
.report-toolbar button { background:#1d3a8a; color:#fff; border:0; border-radius:8px;
  padding:10px 18px; font-size:14px; font-weight:600; cursor:pointer; }
.report-toolbar span { color:#6b7280; font-size:13px; }
.report-page { background:#fff; max-width:210mm; margin:0 auto; padding:20mm 18mm;
  box-shadow:0 2px 16px rgba(0,0,0,.08); box-sizing:border-box; }
.report-head { border-bottom:3px solid #1d3a8a; padding-bottom:16px; margin-bottom:20px; }
.report-head .brand { color:#1d3a8a; font-weight:700; font-size:13px; letter-spacing:.04em; }
.report-head h1 { font-size:24px; margin:8px 0 4px; }
.report-head .subtitle { color:#374151; font-size:15px; margin:0 0 12px; }
.meta { width:100%; border-collapse:collapse; font-size:12.5px; }
.meta th { text-align:left; color:#6b7280; font-weight:600; width:84px; padding:3px 8px 3px 0; vertical-align:top; }
.meta td { padding:3px 16px 3px 0; }
.kpis { display:flex; gap:12px; margin-bottom:22px; }
.kpi { flex:1; border:1px solid #e5e7eb; border-radius:10px; padding:12px 14px; }
.kpi span { display:block; color:#6b7280; font-size:12px; margin-bottom:4px; }
.kpi b { font-size:20px; color:#111827; }
.block { margin-bottom:20px; break-inside:avoid; }
.block h2 { font-size:15px; color:#1d3a8a; border-left:4px solid #1d3a8a; padding-left:8px; margin:0 0 10px; }
.narrative { font-size:13px; line-height:1.8; white-space:pre-wrap; margin:0; }
.gaps { margin:0; padding-left:18px; font-size:13px; line-height:1.8; }
.scores { width:100%; border-collapse:collapse; font-size:12px; }
.scores th { background:#f1f4fb; color:#374151; text-align:left; padding:7px 8px; border-bottom:2px solid #d6ddee; }
.scores td { padding:6px 8px; border-bottom:1px solid #eceff4; vertical-align:top; }
.scores .nowrap { white-space:nowrap; }
.scores .num { text-align:right; font-variant-numeric:tabular-nums; }
.report-foot { margin-top:24px; padding-top:12px; border-top:1px solid #e5e7eb;
  color:#9aa1ad; font-size:10.5px; line-height:1.6; }
@page { size:A4; margin:0; }
@media print {
  .report-root { background:#fff; padding:0; height:auto; overflow:visible; }
  .report-toolbar { display:none; }
  .report-page { box-shadow:none; max-width:none; margin:0; padding:16mm 16mm; }
  .block { break-inside:avoid; }
  .scores tr { break-inside:avoid; }
}
`
